import Button from '@allesauto/core/button';
import Headline from '@allesauto/core/headline';
import NextImage from '@allesauto/core/nextImage';
import SearchDropdown, { Option } from '@allesauto/core/searchDropdown';
import deutschlandFlag from '@allesauto/design/img/deutschland_flag.png';
import translate from '@allesauto/i18n';
import {
  useGoogleMapsGeocode,
  useGoogleMapsPlaceAutocomplete,
} from '@allesauto/utils/apiHooks';
import { consts, HeadlineVariation } from '@allesauto/utils/consts';
import { toUrlJSON } from '@allesauto/utils/funcs';
import { FieldTypeAsset } from '@allesauto/utils/storyblok';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

interface DealerZipcodeTeaserProps {
  _uid: string;
  component: string;
  headline: string;
  image: FieldTypeAsset;
  searchUrl: string;
}

type LocationFilter = {
  location?: Option;
  radius?: number;
  latitude?: number;
  longitude?: number;
};

const defaultLocation: LocationFilter = {
  location: undefined,
  radius: undefined,
  latitude: undefined,
  longitude: undefined,
};

const MIN_AUTOCOMPLETE_LENGTH = 3;

const cssClasses = {
  container: clsx(
    'bg-purple80 min-h-[460px] px-15px py-[50px] relative lg:px-0 monochrome:bg-purple80',
    'gap-[24px] grid sm:grid-cols-1 lg:grid-cols-24'
  ),
  headline: 'hidden lg:block mb-80px',
  mobileHeadline: 'lg:hidden',
  imageContainer: clsx(
    'h-[360px] lg:col-end-13 lg:col-start-2 lg:flex-1 lg:ml-0',
    'relative z-index-[0]'
  ),
  carImage: clsx(
    'sm:max-h-[40%] md:max-h-[60%] md:max-w-[80%] lg:max-h-[80%] lg:max-w-[60%]',
    '!left-[auto] !top-[auto] z-index-[2] lg:!right-[10%]'
  ),
  mapImage:
    'left-0 z-index-[1] h-auto md:object-[40%] lg:object-[10%] xl:object-[15%]',
  textContainer: clsx(
    'flex flex-1 flex-col justify-center lg:col-end-24 lg:col-start-13 '
  ),
  inputsContainer: 'gap-20px grid mb-20px sm:grid-cols-1 lg:grid-cols-2',
  button: 'w-full',
};

const DealerZipcodeTeaser = (props: DealerZipcodeTeaserProps) => {
  const { headline, searchUrl, image } = props;
  const [inputValue, setInputValue] = useState('');
  const [locationFilter, setLocationFilter] = useState(defaultLocation);
  const didMount = useRef(false);
  const router = useRouter();
  const intl = useIntl();

  const onButtonClick = () => {
    if (!locationFilter.location || !locationFilter.radius) return;

    let redirectUrl = '';
    const locationQuery = toUrlJSON(locationFilter, true);

    if (searchUrl.includes('?')) {
      redirectUrl = `${searchUrl}&location=${locationQuery}`;
    } else {
      redirectUrl = `${searchUrl}?location=${locationQuery}`;
    }

    router.push(redirectUrl);
  };

  const countryBoundary = useSelector(
    (state: any) => state.app.googleMaps.countryBoundary
  );

  const { results: places, loading: placesLoading } =
    useGoogleMapsPlaceAutocomplete({
      input: inputValue,
      language: intl.locale || intl.defaultLocale,
      components: countryBoundary,
      minLength: MIN_AUTOCOMPLETE_LENGTH,
    });

  const { result: latLng } = useGoogleMapsGeocode({
    placeId: locationFilter.location?.value as string,
  });

  const onLocationInputChange = debounce((v) => setInputValue(v), 100);

  const radiusOptions = useMemo(() => {
    return consts.PCP.FILTERS.LOCATION_RADIUS_OPTIONS.map((r) => ({
      value: r,
      label: `${r} km`,
    }));
  }, []);

  const resetFields = () => {
    setLocationFilter(defaultLocation);
  };

  const onChange = useCallback((value?: Partial<LocationFilter>) => {
    if (value && (value.location || value.radius)) {
      setLocationFilter((current) => ({ ...current, ...value }));
    } else {
      resetFields();
    }
  }, []);

  useEffect(() => {
    if (latLng && didMount.current) {
      onChange({
        location: locationFilter.location as Option,
        latitude: (latLng.lat && parseFloat(latLng.lat)) || 0,
        longitude: (latLng.lon && parseFloat(latLng.lon)) || 0,
      });
    } else {
      didMount.current = true;
    }
  }, [latLng, locationFilter.location, onChange]);

  const radiusValue = useMemo(() => {
    return radiusOptions.find((r) => r.value === locationFilter.radius) ?? null;
  }, [radiusOptions, locationFilter.radius]);

  return (
    <div {...sbEditable(props)} className={cssClasses.container}>
      <Headline
        headlineTag={HeadlineVariation.H2}
        headlineText={headline}
        headlineColor="text-white"
        className={cssClasses.mobileHeadline}
      />

      <div className={cssClasses.imageContainer}>
        <NextImage
          useCustomLoader={false}
          src={deutschlandFlag}
          alt="Deutschland Flag"
          layout="fill"
          imgClassName={cssClasses.mapImage}
          objectFit="contain"
        />
        {image?.filename && (
          <NextImage
            useCustomLoader={false}
            alt={image?.alt || ''}
            src={image?.filename}
            layout="fill"
            imgClassName={cssClasses.carImage}
            objectFit="contain"
            objectPosition="bottom"
          />
        )}
      </div>
      <div className={cssClasses.textContainer}>
        <Headline
          headlineTag={HeadlineVariation.H2}
          headlineText={headline}
          headlineColor="text-white"
          className={cssClasses.headline}
        />

        <div className={cssClasses.inputsContainer}>
          <SearchDropdown
            onInputChange={onLocationInputChange}
            options={places.map((p) => ({
              value: p.place_id,
              label: p.description,
            }))}
            value={locationFilter.location || null}
            onChange={(value) => {
              if (value) {
                onChange({
                  location: value as Option,
                  latitude: undefined,
                  longitude: undefined,
                });
              } else {
                resetFields(); // x was clicked, reset the whole filter
              }
            }}
            hideDropdownIndicator
            darkBg
            placeholder={translate(
              intl,
              'ui.PCP.FILTERS.LOCATION.LOCATION_PLACEHOLDER'
            )}
            hideNoOptions={
              inputValue.length < MIN_AUTOCOMPLETE_LENGTH || placesLoading
            }
          />

          <SearchDropdown
            id="radiusDropdown"
            options={radiusOptions}
            value={radiusValue}
            onChange={(option) => {
              onChange({
                radius: (option as Option).value as number,
              });
            }}
            placeholder={translate(
              intl,
              'ui.PCP.FILTERS.LOCATION.RADIUS_LABEL'
            )}
            isSearchable={false}
            isClearable={false}
            darkBg
          />
        </div>

        <Button
          fullWidth
          text="Suche Starten"
          type="button"
          customClassName={cssClasses.button}
          whitelabel={true}
          onClick={onButtonClick}
        />
      </div>
    </div>
  );
};

export default DealerZipcodeTeaser;
